import {
    BathroomMedium,
    BedroomMedium,
    LivingRoomMedium
} from '@zoopla-eng/zds/icons';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { Image } from '@zoopla/image';
import { FC } from 'react';
import {
    featurePropertyCardWrapperStyle,
    propertyDetailsContainerStyle,
    styledCardStyle,
    imageStyle
} from '../FeaturedProperties.styles.css';
import {
    agentImageStyle,
    agentImageWrapperStyle,
    agentLinkStyle,
    featureWrapperStyle,
    iconAndTextStyle,
    propertyDetailsStyle,
    propertyImageStyle,
    styledPropertyAddressStyle,
    styledPropertyPriceStyle,
    styledPropertySubPriceStyle,
    styledPropertyTitleStyle
} from './FeaturedPropertyCard.styles.css';

type ImgList = {
    width: number;
    src: string;
}[];

export type Props = {
    property: {
        listingId: string;
        title?: string | null;
        price?: string | null;
        alternativeRentFrequencyLabel?: string | null;
        address?: string | null;
        featuredType?: string | null;
        branch?: {
            logoUrl?: string | null;
            name?: string | null;
            phone?: string | null;
        } | null;
        listingUris?: {
            detail?: string | null;
        } | null;
        features?:
            | ({
                  iconId?: string | null;
                  content?: number | null;
              } | null)[]
            | null;
        image?: {
            src?: string | null;
            responsiveImgList?:
                | ({ src?: string | null; width?: number | null } | null)[]
                | null;
        } | null;
    };
    onClick?: () => void;
    searchIdentifier: string;
};

const featureMap = new Map([
    ['chair', <LivingRoomMedium />],
    ['bath', <BathroomMedium />],
    ['bed', <BedroomMedium />]
]);

const FeaturedPropertyCard: FC<Props> = (props) => {
    const { property, onClick, searchIdentifier } = props;

    const { featuredType } = property;

    const query = new URLSearchParams({
        [featuredType as string]: '1',
        utm_content: 'featured_listing'
    });

    if (searchIdentifier) {
        query.append('search_identifier', searchIdentifier);
    }

    const queryString = query.toString();

    const detailsLink = props.property.listingUris?.detail
        ? `${props.property.listingUris?.detail}?${queryString}`
        : '#';

    const availableFeatures =
        property.features?.filter(
            (feature) =>
                typeof feature?.content === 'number' && feature.content > 0
        ) ?? [];

    // TODO: ZDSU Refactor to simplify the responsive images
    // This was taken from Blueprint
    const generateSrcSet = (img: ImgList) => {
        let res = '';

        img.forEach((item, idx) => {
            res += `${item.src} ${item.width}w`;

            if (idx < img.length - 1) {
                res += ', ';
            }
        });

        return res;
    };

    return (
        <div className={featurePropertyCardWrapperStyle}>
            <div
                className={styledCardStyle}
                onClick={onClick}
                data-testid="featured-property-card"
            >
                {property.image?.src && (
                    <a
                        className={propertyImageStyle}
                        href={detailsLink}
                        data-testid="featured-property-card-link"
                        title="Property image"
                    >
                        <img
                            className={imageStyle}
                            src={property.image.src}
                            alt="Feature property"
                            srcSet={generateSrcSet(
                                property.image?.responsiveImgList?.map((i) => ({
                                    src: i?.src ?? '',
                                    width: i?.width ?? 0
                                })) ?? []
                            )}
                        />
                    </a>
                )}
                <div className={propertyDetailsContainerStyle}>
                    <a className={propertyDetailsStyle} href={detailsLink}>
                        <p
                            className={`${styledPropertyTitleStyle} ${fontGroups.knife}`}
                        >
                            {property.title}
                        </p>

                        <p
                            className={`${styledPropertyPriceStyle} ${fontGroups.whisk}`}
                        >
                            {property.price}
                        </p>
                        {property.alternativeRentFrequencyLabel && (
                            <p
                                className={`${styledPropertySubPriceStyle} ${fontGroups.knife}`}
                                data-testid={'sub-price'}
                            >
                                {property.alternativeRentFrequencyLabel}
                            </p>
                        )}

                        <p
                            className={`${styledPropertyAddressStyle} ${fontGroups.knife}`}
                        >
                            {property.address}
                        </p>
                        <ul className={featureWrapperStyle}>
                            {availableFeatures.map((item, index) => {
                                // TODO: ZDSU change how this works
                                // backend shouldn't dictate
                                // specific UI, layover from fullstack Perl.
                                // Unfortunately a feature only has an iconId so for now
                                // we'll use that as a "feature ID"
                                const { iconId: featureId, content } =
                                    item ?? {};
                                const icon = featureMap.get(featureId || '');

                                if (!icon) {
                                    return null;
                                }

                                return (
                                    <li
                                        key={`${index}-${featureId}`}
                                        className={iconAndTextStyle}
                                    >
                                        {icon}
                                        <p className={fontGroups.knife}>
                                            {content}
                                        </p>
                                    </li>
                                );
                            })}
                        </ul>
                    </a>
                    <a className={agentLinkStyle} href={detailsLink}>
                        <div className={agentImageWrapperStyle}>
                            <Image
                                src={property.branch?.logoUrl ?? ''}
                                alt={property.branch?.name ?? ''}
                                className={agentImageStyle}
                            />
                        </div>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default FeaturedPropertyCard;
